import { useEffect, useState } from "react";
import { useAuthStore, type MfaSetup } from "../stores/auth";
import { ApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/input";

/**
 * Activation de la double authentification : QR à scanner, code de
 * confirmation, puis codes de secours affichés UNE seule fois. Utilisé par le
 * profil (activation volontaire) et par l'écran bloquant de première
 * connexion (activation obligatoire).
 */
export function MfaEnrolment({ onDone }: { onDone?: () => void }) {
  const setupMfa = useAuthStore((s) => s.setupMfa);
  const enableMfa = useAuthStore((s) => s.enableMfa);
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setupMfa()
      .then(setSetup)
      .catch(() => setError("Impossible de préparer la double authentification"));
  }, [setupMfa]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      setRecovery(await enableMfa(code));
    } catch (e) {
      setError(e instanceof ApiError && e.code === "invalid_code" ? "Code refusé : vérifiez l'heure de votre téléphone et réessayez." : "Activation impossible");
    } finally {
      setBusy(false);
    }
  };

  if (recovery) {
    return (
      <div className="mfa">
        <p className="mfa__ok">Double authentification activée.</p>
        <p>
          Conservez ces codes de secours en lieu sûr : chacun ouvre une session <strong>une seule
          fois</strong> si vous n'avez plus votre téléphone. Ils ne seront plus jamais affichés.
        </p>
        <ul className="mfa__codes">
          {recovery.map((c) => (
            <li key={c}>
              <code>{c}</code>
            </li>
          ))}
        </ul>
        <Button variant="primary" onClick={() => onDone?.()}>
          J'ai noté mes codes
        </Button>
      </div>
    );
  }

  return (
    <div className="mfa">
      <ol className="mfa__steps">
        <li>
          Installez une application d'authentification (Google Authenticator, Microsoft Authenticator,
          1Password…).
        </li>
        <li>Scannez ce code, ou saisissez la clé à la main.</li>
        <li>Entrez le code à six chiffres affiché pour confirmer.</li>
      </ol>
      {setup ? (
        <div className="mfa__qr">
          <img src={setup.qrDataUrl} alt="Code QR à scanner avec votre application d'authentification" width={220} height={220} />
          <p className="mfa__secret">
            Clé manuelle : <code>{setup.secret}</code>
          </p>
        </div>
      ) : (
        <p className="muted">Préparation…</p>
      )}
      <Field label="Code à six chiffres">
        <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" />
      </Field>
      {error ? (
        <p className="login__error" role="alert">
          {error}
        </p>
      ) : null}
      <Button variant="primary" onClick={confirm} disabled={busy || !setup || code.replace(/\s/g, "").length !== 6}>
        Activer
      </Button>
    </div>
  );
}
